import { createFileRoute, Outlet } from '@tanstack/react-router'
import { MailIcon, AlertTriangleIcon, LayoutGridIcon, MoveHorizontalIcon } from 'lucide-react'
import { usePreviewMode } from '@/components/custom/route-transition/surface/route-preview'
import { TransitionTabsSurface } from './-components/shared/transition-tabs'
import { PanelBlockPending } from './-components/app-layout-skeleton'

// Layout for the super-admin dev playground. Each section (email templates, captured errors, UI components,
// the transition repro) is its OWN route under here, and the tab bar is a TRANSITION TABS SURFACE — the same
// declarative strip+pager the report tabs run on (see -components/shared/transition-tabs.tsx): the tabs are
// real <Link>s (middle-clickable, shareable, reload-stable) whose underline rides the swipe, and on a phone the
// four tabs are swipeable panes. The bare `/dev-resources` redirects to the emails tab (dev-resources.index).
//
// `scrollablePanes` is the load-bearing half here: this page lives in the app's FIXED-HEIGHT shell (the framed
// <main>), and a pager clips its own vertical overflow so a vertical drag falls through instead of being
// swallowed — so the one shared scroll container this layout used to wrap the <Outlet/> in could not survive
// the pager (everything past one pane-height would have been unreachable, with no scrollable ancestor left to
// reach it). Each pane owns its scroller instead, exactly like the report tabs, which also gives each tab its
// own scroll position.
export const Route = createFileRoute('/_app/dev-resources')({
  component: RouteComponent,
  pendingComponent: PanelBlockPending,
})

const DEV_RESOURCES_SCOPE = '/dev-resources'

const TABS = [
  { segment: 'emails', label: 'Email templates', icon: MailIcon },
  { segment: 'errors', label: 'Captured errors', icon: AlertTriangleIcon },
  { segment: 'components', label: 'Components', icon: LayoutGridIcon },
  { segment: 'transitions', label: 'Transitions', icon: MoveHorizontalIcon },
] as const

// The pane: the page padding and the centred max-width the header above shares, now INSIDE the pane so they
// travel with the page (padding on a container outside the stage doesn't). The max-width is applied to the
// pane's CONTENT rather than the pane, because the pane is the slide's opaque cover — narrowing it would let
// the pane beneath show through the gutters on a wide window. Each route renders a single root element.
const PANE = 'px-6 pt-6 pb-6 sm:px-8 sm:pt-8 sm:pb-8 [&>*]:mx-auto [&>*]:w-full [&>*]:max-w-6xl'

function RouteComponent() {
  // Inside a live preview pinned at this surface (a tab swipe) or below it (the transition repro's own pagers,
  // see dev-resources.transitions.tsx) this layout is the pane CONTENT — the header and the tab strip are live
  // chrome around the pager and must not render into every preview slot.
  if (usePreviewMode(DEV_RESOURCES_SCOPE) === 'passthrough') return <Outlet />

  return (
    <div className="flex h-full flex-col">
      <div className="px-6 pt-6 sm:px-8 sm:pt-8">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-1">
          <h1 className="text-2xl font-bold">Developer resources</h1>
          <p className="text-muted-foreground text-sm">
            Internal dev playground — preview email templates, triage captured errors, and browse UI components.
          </p>
        </div>
      </div>
      <TransitionTabsSurface scope={DEV_RESOURCES_SCOPE} tabs={TABS} scrollablePanes contentClassName={PANE} />
    </div>
  )
}
